import { useState } from "react";
import { Alert } from "react-bootstrap";
import Select, { StylesConfig } from "react-select";
import moment from "moment";
import {
  Field,
  FieldType,
  FilterBar,
  FixedToolbarItem,
  FlexToolbarItem,
  IconButton,
  LinePlot,
  Toolbar,
  useAATheme,
  useModelColorMap,
  useReactSelectTheme,
} from "@bagel-web/components";
import { Usage } from "../types";
import { formatCurrency, formatNumber } from "../util";
import useFilters from "../filter/useFilters";
import { useQuery } from "@tanstack/react-query";
import { usageQueries } from "../queries";

type FieldOption = {
  value: string;
  label: string;
};

const fieldOptions: FieldOption[] = [
  { value: "requests", label: "Requests" },
  { value: "input_tokens", label: "Input tokens" },
  { value: "output_tokens", label: "Output tokens" },
  { value: "cost", label: "Estimated cost" },
];

const fieldStyleOverrides: StylesConfig<FieldOption, false> = {
  container: (baseStyles) => ({
    ...baseStyles,
    minWidth: "180px",
  }),
};

const defaultStartDate = moment().subtract(1, "month").format("YYYY-MM-DD");
const defaultEndDate = moment().format("YYYY-MM-DD");
const fields: Array<Field> = [
  {
    name: "usageDetailStartDate",
    label: "Start Date",
    type: FieldType.Date,
    required: true,
    defaultValue: defaultStartDate,
  },
  {
    name: "usageDetailEndDate",
    label: "End Date",
    type: FieldType.Date,
    required: true,
    defaultValue: defaultEndDate,
  },
];

const UsageDetailReport = ({
  organizationName,
  showTokenCounts = false,
  getCost,
  fetchUsageBetween,
  exportUsageDetailReport,
}: {
  organizationName?: string;
  showTokenCounts?: boolean;
  getCost?: (usage: Usage) => number;
  fetchUsageBetween: (startDate: string, endDate: string) => Promise<Usage[]>;
  exportUsageDetailReport?: (
    usage: Usage[],
    startDate: string,
    endDate: string,
    organization?: string
  ) => void;
}) => {
  const { getColors } = useModelColorMap();
  const [selectedField, setSelectedField] = useState<string>("requests");

  const { filterCriteria, handleSetFilters, handleClearFilter } =
    useFilters(fields);

  const startDate = filterCriteria.usageDetailStartDate?.value || "";
  const endDate = filterCriteria.usageDetailEndDate?.value || "";

  const { isDarkMode } = useAATheme();

  const { data: usage } = useQuery({
    queryKey: usageQueries.getUsageBetween({
      startDate: startDate,
      endDate: endDate,
    }),
    queryFn: () =>
      filterCriteria.usageDetailStartDate?.value &&
      filterCriteria.usageDetailEndDate?.value &&
      endDate >= startDate
        ? fetchUsageBetween(
            startDate,
            moment(endDate).add(1, "days").format("YYYY-MM-DD")
          )
        : [],
  });

  const allModelNames = Array.from(
    new Set([...(usage?.map((u) => u.service_alias_name) || [])])
  );

  const datasets = usage
    ? allModelNames.map((modelName) => {
        return {
          label: modelName,
          borderColor: getColors(modelName).borderColor,
          backgroundColor: getColors(modelName).backgroundColor,
          data: usage
            .filter((u) => u.service_alias_name === modelName)
            .map((u) => {
              if (selectedField === "cost" && getCost) {
                return { x: u.timestamp, y: getCost(u) };
              } else {
                return { x: u.timestamp, y: u[selectedField as keyof Usage] };
              }
            }),
        };
      })
    : undefined;

  const selectedFieldOption = fieldOptions.find(
    (option) => option.value === selectedField
  );

  const options: { [key: string]: object } = {};

  if (selectedField === "cost") {
    options.scales = {
      y: {
        ticks: {
          callback: (value: string | number) => {
            return Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(value as number);
          },
        },
      },
    };
  }

  const formatValue = (value: string | number) => {
    return selectedField === "cost"
      ? formatCurrency(Number(value))
      : formatNumber(Number(value));
  };

  let linePlotOptions = LinePlot.getDefaultOptions({ isDarkMode });
  linePlotOptions = {
    ...linePlotOptions,
    scales: {
      ...linePlotOptions?.scales,
      x: {
        ...linePlotOptions?.scales?.x,
        type: "time",
        time: { unit: "day", tooltipFormat: "MMMM DD, YYYY" },
        suggestedMin: moment().subtract(1, "months").format("YYYY-MM-DD"),
        suggestedMax: moment().format("YYYY-MM-DD"),
      },
      y: {
        ...linePlotOptions?.scales?.y,
        ticks: {
          ...linePlotOptions?.scales?.y?.ticks,
          callback: (value) => {
            return formatValue(value);
          },
        },
      },
    },
    plugins: {
      ...linePlotOptions?.plugins,
      legend: {
        ...linePlotOptions?.plugins?.legend,
        position: "right",
      },
      tooltip: {
        ...linePlotOptions?.plugins?.tooltip,
        callbacks: {
          ...linePlotOptions?.plugins?.tooltip?.callbacks,
          label: (tooltipItems) => {
            const value = (tooltipItems.raw as { y: number }).y;
            return formatValue(value);
          },
        },
      },
    },
  };

  return (
    <div>
      <Toolbar className="mb-3">
        {showTokenCounts && (
          <FlexToolbarItem>
            <Select<FieldOption, false>
              value={selectedFieldOption ?? null}
              options={fieldOptions}
              styles={fieldStyleOverrides}
              theme={useReactSelectTheme()}
              isClearable={false}
              isSearchable={false}
              onChange={(newValue) => {
                if (newValue) {
                  setSelectedField(newValue.value);
                }
              }}
            />
          </FlexToolbarItem>
        )}

        <FlexToolbarItem>
          <FilterBar
            fields={fields}
            criteria={filterCriteria}
            onSetFilters={handleSetFilters}
            onClear={handleClearFilter}
          />
        </FlexToolbarItem>

        {showTokenCounts && exportUsageDetailReport && (
          <FixedToolbarItem>
            <IconButton
              disabled={
                !organizationName ||
                !usage ||
                usage.length === 0 ||
                startDate > endDate
              }
              size="lg"
              className="bi-download"
              tooltip="Download CSV"
              onClick={() =>
                usage &&
                exportUsageDetailReport(
                  usage,
                  startDate,
                  endDate,
                  organizationName
                )
              }
            />
          </FixedToolbarItem>
        )}
      </Toolbar>

      {startDate > endDate && (
        <Alert variant="warning">
          Please select an end date <b>on or after</b> the start date.
        </Alert>
      )}

      <LinePlot
        height="350px"
        data={
          datasets
            ? {
                datasets,
              }
            : undefined
        }
        options={linePlotOptions}
      />
    </div>
  );
};

export default UsageDetailReport;
